<template>
    <!-- Backdrop: only exists while the drawer is open, and only below md. Tapping
         it closes the drawer, which is the gesture people try first. -->
    <div
        v-if="sidebarOpen"
        class="fixed inset-0 z-30 bg-black/50 md:hidden"
        @click="closeSidebar"
    />

    <!-- Below md the sidebar is an off-canvas drawer; at md+ it is the static
         column it has always been. A 240px sidebar pinned open on a 375px screen
         left 135px for content, which is why nothing in this console was usable
         on a phone. -->
    <aside
        class="w-60 shrink-0 flex flex-col bg-white dark:bg-[#060a12] border-r border-slate-200 dark:border-slate-800
               fixed inset-y-0 left-0 z-40 transition-transform duration-200 ease-out
               md:static md:translate-x-0 md:transition-none"
        :class="sidebarOpen ? 'translate-x-0' : '-translate-x-full'"
    >
        <!-- Close affordance, mobile only: the backdrop handles most cases but an
             explicit control is what a keyboard or screen-reader user needs. -->
        <button
            class="md:hidden absolute top-4 right-3 w-11 h-11 flex items-center justify-center rounded-lg text-slate-400 hover:text-slate-200"
            type="button"
            :aria-label="$t('general.close')"
            @click="closeSidebar"
        >
            <i class="pi pi-times" />
        </button>
        <!-- pr-14 below md keeps the wordmark clear of the close control, which the
             capture pass caught sitting on top of "Admin". -->
        <div class="flex items-center gap-3 px-5 py-5 pr-14 md:pr-5 border-b border-slate-200 dark:border-slate-800/60">
            <!-- The mark is dropped below md: the close control needs that corner,
                 and 240px minus the icon left too little for "ComplyIT Admin", which
                 then broke across two lines. The topbar already identifies the app. -->
            <div class="w-9 h-9 rounded-lg bg-primary-600/20 hidden md:flex items-center justify-center">
                <i class="pi pi-server text-primary-500 text-xl" />
            </div>
            <div>
                <div class="font-bold text-lg leading-tight dark:text-white">Comply<span class="brand-it">IT</span> <span class="text-slate-400 font-medium text-sm">Admin</span></div>
                <div class="text-[9px] tracking-widest text-slate-400">{{ $t('nav.tagline') }}</div>
                <button
                    class="text-[10px] text-slate-500 mt-0.5 hover:text-primary-400 cursor-pointer"
                    type="button"
                    @click="aboutDialog?.showDialog()"
                >
                    v{{ version }}
                </button>
            </div>
        </div>

        <nav class="flex-1 min-h-0 overflow-y-auto px-3 mt-2 pb-4 flex flex-col">
            <template v-if="!isInstalater">
                <div class="nav-group">{{ $t('nav.overview') }}</div>
                <RouterLink :to="{ name: 'dashboard' }" class="nav-item" active-class="nav-item-active">
                    <i class="pi pi-th-large" /> {{ $t('nav.dashboard') }}
                </RouterLink>
            </template>

            <div class="nav-group mt-6">{{ $t('nav.settings_group') }}</div>
            <RouterLink :to="{ name: 'customers' }" class="nav-item" active-class="nav-item-active">
                <i class="pi pi-building" /> {{ $t('nav.customers') }}
            </RouterLink>
            <RouterLink :to="{ name: 'instances' }" class="nav-item" active-class="nav-item-active">
                <i class="pi pi-server" /> {{ $t('nav.instances') }}
            </RouterLink>
            <RouterLink v-if="!isInstalater" :to="{ name: 'users' }" class="nav-item" active-class="nav-item-active">
                <i class="pi pi-users" /> {{ $t('nav.users') }}
            </RouterLink>
            <RouterLink v-if="isAdmin" :to="{ name: 'mailboxes' }" class="nav-item" active-class="nav-item-active">
                <i class="pi pi-inbox" /> {{ $t('nav.mailboxes') }}
            </RouterLink>
            <RouterLink v-if="isAdmin" :to="{ name: 'mail' }" class="nav-item" active-class="nav-item-active">
                <i class="pi pi-envelope" /> {{ $t('nav.mail') }}
            </RouterLink>
            <RouterLink v-if="isAdmin" :to="{ name: 'support' }" class="nav-item" active-class="nav-item-active">
                <i class="pi pi-ticket" /> {{ $t('nav.support') }}
                <span v-if="openTickets > 0" class="nav-badge">{{ openTickets }}</span>
            </RouterLink>
            <!-- Oddaljen dostop sits beside the ticket queue because that is where
                 a request arrives from, and carries a badge for the same reason
                 tickets do: somebody is waiting in front of a broken appliance. -->
            <RouterLink :to="{ name: 'remote-access' }" class="nav-item" active-class="nav-item-active">
                <i class="pi pi-lifebuoy" /> {{ $t('nav.remote_access') }}
                <span v-if="pendingRemote > 0" class="nav-badge">{{ pendingRemote }}</span>
            </RouterLink>
            <RouterLink v-if="isAdmin" :to="{ name: 'settings' }" class="nav-item" active-class="nav-item-active">
                <i class="pi pi-cog" /> {{ $t('nav.settings') }}
            </RouterLink>

            <template v-if="!isInstalater">
                <div class="nav-group mt-6">{{ $t('nav.billing_group') }}</div>
                <RouterLink :to="{ name: 'plans' }" class="nav-item" active-class="nav-item-active">
                    <i class="pi pi-box" /> {{ $t('nav.plans') }}
                </RouterLink>
                <RouterLink :to="{ name: 'releases' }" class="nav-item" active-class="nav-item-active">
                    <i class="pi pi-cloud-upload" /> {{ $t('nav.releases') }}
                </RouterLink>
                <RouterLink :to="{ name: 'billing' }" class="nav-item" active-class="nav-item-active">
                    <i class="pi pi-euro" /> {{ $t('nav.billing') }}
                </RouterLink>
            </template>

            <!-- Help — pinned to the bottom, visible to every role incl. instalater. -->
            <RouterLink :to="{ name: 'help' }" class="nav-item mt-auto" active-class="nav-item-active">
                <i class="pi pi-question-circle" /> {{ $t('nav.help') }}
            </RouterLink>
        </nav>

        <AboutDialog ref="aboutDialog" />
    </aside>
</template>

<script lang="ts" setup>
    import { computed, onMounted, ref, useTemplateRef, watch } from 'vue'
    import { useRoute } from 'vue-router'

    import AboutDialog from '@/layouts/components/AboutDialog.vue'
    import { UserRole } from '@/enums/admin'
    import { getUserRole } from '@/libs/auth'
    import { usePoller } from '@/libs/poller'
    import { APP_VERSION } from '@/libs/version'
    import { useLayout } from '@/layouts/layout'
    import { useFetchStore } from '@/stores/fetch'
    import type { IMailStatus } from '@/types/Admin'

    const route = useRoute()
    const { sidebarOpen, closeSidebar } = useLayout()

    // Close the drawer whenever navigation happens. Without this, tapping a nav
    // item on a phone leaves the drawer covering the page it just opened.
    watch(() => route.fullPath, closeSidebar)

    const version = APP_VERSION

    // The instalater role gets a trimmed nav: only Stranke (read-only) + Instance.
    const isInstalater = computed(() => getUserRole() === UserRole.Instalater)

    // Settings (SMTP + mail redirect) is admin-only.
    const isAdmin = computed(() => getUserRole() === UserRole.Admin)

    const aboutDialog = useTemplateRef<InstanceType<typeof AboutDialog>>('aboutDialog')

    // Open-ticket badge on "Podpora": polled from /mail/status (admin only). A
    // failure (e.g. mail not configured) simply leaves the badge hidden. The
    // timer is owned by libs/poller — registered once, cleared on unmount and
    // idle while the tab is hidden.
    const TICKET_POLL_MS = 60_000

    const fetchStore  = useFetchStore()
    const openTickets = ref(0)

    const loadOpenTickets = async () => {
        if (!isAdmin.value) return
        const { statusCode, data } = await fetchStore.fetch('/mail/status').get().json<IMailStatus>()
        if (statusCode.value === 401) {
            ticketPoll.dispose()
            return
        }
        if (statusCode.value === 200 && data.value) openTickets.value = data.value.open_tickets
    }

    const ticketPoll = usePoller(loadOpenTickets, TICKET_POLL_MS)

    // Remote-access requests waiting for an answer. Polled for EVERY console role,
    // not just admins: a non-admin cannot approve, but seeing that a customer is
    // waiting is how somebody gets told to go and approve it.
    const pendingRemote = ref(0)

    const loadPendingRemote = async () => {
        const { statusCode, data } = await fetchStore.fetch('/support/').get().json<{ state: string }[]>()
        if (statusCode.value === 401) {
            remotePoll.dispose()
            return
        }
        if (statusCode.value === 200 && data.value) {
            pendingRemote.value = (data.value ?? []).filter(s => s.state === 'zahtevana').length
        }
    }

    const remotePoll = usePoller(loadPendingRemote, TICKET_POLL_MS)

    onMounted(() => {
        void remotePoll.trigger()
        remotePoll.start()

        if (!isAdmin.value) return
        void ticketPoll.trigger()
        ticketPoll.start()
    })
</script>

<style scoped>
    .brand-it {
        color: #f7901d;
    }

    .nav-group {
        padding: 0.45rem 0.5rem;
        margin-bottom: 0.15rem;
        font-size: 0.7rem;
        font-weight: 700;
        letter-spacing: 0.08em;
        color: var(--color-slate-400);
    }

    .nav-item {
        display: flex;
        align-items: center;
        gap: 0.55rem;
        padding: 0.45rem 0.75rem 0.45rem 1.1rem;
        border-radius: 0.5rem;
        font-size: 0.82rem;
        color: var(--color-slate-500);
        margin-bottom: 1px;
    }

    .dark .nav-item {
        color: var(--color-slate-400);
    }

    .nav-item:hover {
        background: color-mix(in srgb, var(--color-primary-500) 8%, transparent);
    }

    .nav-item-active {
        color: var(--color-primary-500);
        background: color-mix(in srgb, var(--color-primary-500) 12%, transparent);
        outline: 1px solid color-mix(in srgb, var(--color-primary-500) 35%, transparent);
        font-weight: 600;
    }

    .nav-badge {
        margin-left: auto;
        min-width: 1.15rem;
        height: 1.15rem;
        padding: 0 0.35rem;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        border-radius: 999px;
        background: var(--color-primary-500);
        color: #fff;
        font-size: 0.68rem;
        font-weight: 700;
        line-height: 1;
    }
</style>
